// src/components/billing/BillingPanel.js
import React from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import ButtonComponent from "../ui/ButtonComponent";
import CheckoutClickwrapModal from "./ClickwrapModal";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Explicit mode switch (frontend)
const STRIPE_MODE = (process.env.REACT_APP_STRIPE_MODE || "test").toLowerCase();
const modeKey = STRIPE_MODE === "live" ? "live" : "test";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const CREATE_CHECKOUT_URL =
  process.env.REACT_APP_CREATE_STRIPE_CHECKOUT_URL ||
  "https://createstripecheckoutsession-774073159618.us-central1.run.app";

const CREATE_PORTAL_URL =
  process.env.REACT_APP_CREATE_STRIPE_PORTAL_URL ||
  "https://createstripeportallink-774073159618.us-central1.run.app";

function normalizeSubStatus(status) {
  const s = String(status || "").toLowerCase();
  return s || null;
}

function shouldAllowPortal(status) {
  return !!status;
}

function shouldOfferActivation({ assignedPriceId, status }) {
  const st = normalizeSubStatus(status);
  const hasAssigned = !!assignedPriceId;
  const alreadyActive = st === "active" || st === "trialing";
  return hasAssigned && !alreadyActive;
}

const BillingPanel = ({
  embedded = false,
  showBanners = true,
  allowStartSubscription = true,
  variant = "card",
  hideTitle = false,
}) => {
  const [billingSummary, setBillingSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  // ✅ clickwrap modal
  const [clickwrapOpen, setClickwrapOpen] = React.useState(false);

  // banners derived from query params
  const [checkoutState, setCheckoutState] = React.useState("none"); // success|cancel|none
  const [portalState, setPortalState] = React.useState("none"); // back|none
  const [sourceTag, setSourceTag] = React.useState(null);
  const [reasonTag, setReasonTag] = React.useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const orgId = sessionStorage.getItem("organizationId");
    if (!orgId) {
      setLoading(false);
      return;
    }

    const summaryRef = doc(db, "organizations", orgId, "billing", "summary");
    const un = onSnapshot(
      summaryRef,
      (snap) => {
        const data = snap.data() || null;
        setBillingSummary(data);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return un;
  }, []);

  // Handle checkout/portal banners via query params, then clean the URL
  React.useEffect(() => {
    if (embedded) return;

    const params = new URLSearchParams(location.search);

    const state = params.get("checkout");
    if (state === "success") setCheckoutState("success");
    else if (state === "cancel") setCheckoutState("cancel");
    else setCheckoutState("none");

    const portal = params.get("portal");
    setPortalState(portal === "back" ? "back" : "none");

    const src = params.get("source");
    const rsn = params.get("reason");
    setSourceTag(src || null);
    setReasonTag(rsn || null);

    if (state || portal || src || rsn) {
      navigate(location.pathname, { replace: true });
    }
  }, [embedded, location.pathname, location.search, navigate]);

  const derived = React.useMemo(() => {
    const data = billingSummary || {};

    const stripeCustomerIdByMode = data.stripeCustomerIdByMode || {};
    const customerIdForMode = stripeCustomerIdByMode?.[modeKey] || null;

    const subByMode = data.subscriptionStatusByMode || {};
    const status = subByMode?.[modeKey] || data.subscriptionStatus || null;

    const priceIdByMode = data.priceIdByMode || {};
    const priceDescByMode = data.priceDescriptionByMode || {};
    const priceId = priceIdByMode?.[modeKey] || data.priceId || null;
    const priceDescription = priceDescByMode?.[modeKey] || data.priceDescription || null;

    const assignedPriceIdByMode = data.assignedPriceIdByMode || {};
    const assignedPlanNameByMode = data.assignedPlanNameByMode || {};
    const assignedPriceId = assignedPriceIdByMode?.[modeKey] || null;
    const assignedPlanName = assignedPlanNameByMode?.[modeKey] || null;

    const assignedPromotionByMode = data.assignedPromotionByMode || {};
    const promo = assignedPromotionByMode?.[modeKey] || null;
    const promoEndsAt = promo?.endsAt || null;
    const promoCouponId = promo?.couponId || null;
    const promoCouponDescription = promo?.description || null;

    const currentPeriodEndByMode = data.currentPeriodEndByMode || {};
    const lastInvoicePaidAtByMode = data.lastInvoicePaidAtByMode || {};
    const nextPaymentAttemptAtByMode = data.nextPaymentAttemptAtByMode || {};
    const lastInvoiceFailedAtByMode = data.lastInvoiceFailedAtByMode || {};

    return {
      customerIdForMode,
      status,

      priceId,
      priceDescription,

      assignedPriceId,
      assignedPlanName,

      promoEndsAt,
      promoCouponId,
      promoCouponDescription,

      currentPeriodEnd: currentPeriodEndByMode?.[modeKey] || data.currentPeriodEnd || null,
      lastInvoicePaidAt: lastInvoicePaidAtByMode?.[modeKey] || data.lastInvoicePaidAt || null,
      nextPaymentAttemptAt:
        nextPaymentAttemptAtByMode?.[modeKey] || data.nextPaymentAttemptAt || null,
      lastInvoiceFailedAt:
        lastInvoiceFailedAtByMode?.[modeKey] || data.lastInvoiceFailedAt || null,

      lastStripeEvent: data.lastStripeEvent || null,
    };
  }, [billingSummary]);

  // ✅ Now only opens the modal (no redirect)
  const handleActivateSubscription = () => {
    if (!auth.currentUser) return;
    const priceId = derived.assignedPriceId;
    if (!priceId) return;
    setClickwrapOpen(true);
  };

  // ✅ Called by modal "Continue to Checkout"
  const handleContinueToCheckout = async (acceptance) => {
    if (!auth.currentUser) return;

    const priceId = derived.assignedPriceId;
    if (!priceId) return;

    setIsRedirecting(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const successUrl = `${window.location.origin}${location.pathname}?checkout=success`;
      const cancelUrl = `${window.location.origin}${location.pathname}?checkout=cancel`;

      // Keep your existing payload shape; CF can ignore priceId if it uses assignedPriceIdByMode
      const clientRequestId =
  (window.crypto && typeof window.crypto.randomUUID === "function")
    ? window.crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

        const payload = {
        data: {
            priceId,
            successUrl,
            cancelUrl,
            acceptance: {
            accepted: true,
            clientRequestId,
            // Optional debug only (server will not trust these)
            saasVersion: "v1",
            privacyVersion: "v1",
            },
        },
        };
      const { data } = await axios.post(CREATE_CHECKOUT_URL, payload, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to initialize.");

      const sessionId = data?.data?.sessionId || data?.sessionId;
      if (sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) throw error;
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      throw new Error("No checkout session returned from server.");
    } catch (err) {
      console.error("Stripe checkout error:", err);
      setIsRedirecting(false);
    }
  };

  const handleManageBilling = async () => {
    if (!auth.currentUser) return;

    setIsRedirecting(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const returnUrl = embedded
        ? `${window.location.origin}/settings`
        : `${window.location.origin}${location.pathname}?portal=back`;

      const { data } = await axios.post(
        CREATE_PORTAL_URL,
        { returnUrl },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      throw new Error("No portal URL returned.");
    } catch (err) {
      console.error("Stripe portal error:", err);
      setIsRedirecting(false);
    }
  };

  const Banners = () => {
    if (!showBanners) return null;

    const st = normalizeSubStatus(derived.status);
    const showProcessing = checkoutState === "success" && st !== "active" && st !== "trialing";

    return (
      <Stack spacing={1} sx={{ mb: 2 }}>
        {checkoutState === "success" && (st === "active" || st === "trialing") && (
          <Alert severity="success">Payment successful. Your subscription is now active.</Alert>
        )}

        {showProcessing && (
          <Alert severity="info">Payment successful. Finalizing your subscription…</Alert>
        )}

        {checkoutState === "cancel" && (
          <Alert severity="warning">Checkout canceled. No changes were made.</Alert>
        )}

        {portalState === "back" && <Alert severity="info">You’re back from the billing portal.</Alert>}

        {reasonTag === "payment_failed" && (
          <Alert severity="error">
            Your recent payment failed. Please update your payment method in the billing portal.
          </Alert>
        )}

        {sourceTag === "invoice" && (
          <Alert severity="info">You opened this page from an invoice link.</Alert>
        )}

        {st === "past_due" && (
          <Alert severity="error">
            Your subscription is past due. Please update your payment method in the billing portal.
          </Alert>
        )}
      </Stack>
    );
  };

  const AssignedPlanBlock = () => {
    const hasAssigned = !!derived.assignedPriceId;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Assigned Plan
        </Typography>

        {!hasAssigned ? (
          <Alert severity="warning">
            No plan is assigned to this organization yet. Please contact Kinova Support to activate billing.
          </Alert>
        ) : (
          <>
            <Typography variant="body2">
              <strong>Plan:</strong>{" "}
              {derived.assignedPlanName || derived.priceDescription || derived.assignedPriceId}
            </Typography>

            {derived.promoCouponDescription && (
              <Typography variant="body2">
                <strong>Promotion:</strong> {derived.promoCouponDescription}
              </Typography>
            )}

            {derived.promoEndsAt && (
              <Typography variant="body2">
                <strong>Promo ends:</strong> {fmt(derived.promoEndsAt)}
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  const StatusBlock = () => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Status
      </Typography>
      <Typography variant="body2">
        <strong>Mode:</strong> {modeKey}
      </Typography>
      <Typography variant="body2">
        <strong>Subscription:</strong> {derived.status || "—"}
      </Typography>
      <Typography variant="body2">
        <strong>Current plan (Stripe):</strong>{" "}
        {derived.priceDescription || derived.priceId || "—"}
      </Typography>
    </Box>
  );

  const PaymentsBlock = () => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Payments
      </Typography>
      <Typography variant="body2">
        <strong>Last payment:</strong> {fmt(derived.lastInvoicePaidAt)}
      </Typography>
      <Typography variant="body2">
        <strong>Next payment:</strong> {fmt(derived.nextPaymentAttemptAt || derived.currentPeriodEnd)}
      </Typography>
      {derived.lastInvoiceFailedAt && (
        <Typography variant="body2">
          <strong>Last failure:</strong> {fmt(derived.lastInvoiceFailedAt)}
        </Typography>
      )}
    </Box>
  );

  const ActionsBlock = () => {
    const st = normalizeSubStatus(derived.status);
    const canPortal = shouldAllowPortal(st) && !!derived.customerIdForMode;

    const canActivate =
      allowStartSubscription &&
      shouldOfferActivation({ assignedPriceId: derived.assignedPriceId, status: st });

    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Manage
        </Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <ButtonComponent
            label="Manage Billing (Portal)"
            onClick={handleManageBilling}
            isLoading={isRedirecting}
            disabled={!canPortal}
          />

          {canActivate && (
            <ButtonComponent
              label="Activate Subscription"
              onClick={handleActivateSubscription}
              isLoading={isRedirecting}
              type="secondary"
            />
          )}
        </Stack>

        {!derived.customerIdForMode && (
          <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
            Billing portal is unavailable until a Stripe customer exists for this org in {modeKey} mode.
          </Typography>
        )}

        <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
          Use the Stripe Customer Portal to update payment methods or download invoices.
        </Typography>
      </Box>
    );
  };

  const content = () => {
    if (loading) return <CircularProgress />;

    return (
      <>
        <Banners />
        <AssignedPlanBlock />
        <Divider sx={{ my: 2 }} />
        <StatusBlock />
        <Divider sx={{ my: 2 }} />
        <PaymentsBlock />
        <Divider sx={{ my: 2 }} />
        <ActionsBlock />

        {/* ✅ Clickwrap modal */}
        <CheckoutClickwrapModal
          open={clickwrapOpen}
          onClose={() => setClickwrapOpen(false)}
          onContinue={async (acceptance) => {
            setClickwrapOpen(false);
            await handleContinueToCheckout(acceptance);
          }}
          isLoading={isRedirecting}
        />
      </>
    );
  };

  if (variant === "plain") {
    return (
      <Box sx={{ width: "100%", maxWidth: 900 }}>
        {!hideTitle && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            Billing & Subscription
          </Typography>
        )}
        {content()}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 900 }}>
      <Card>
        <CardContent>{content()}</CardContent>
      </Card>
    </Box>
  );
};

export default BillingPanel;